import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { supabase } from '../../supabaseClient';
import { AppSearch } from '../../components/ui';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { useNotifications } from '../../contexts/NotificationContext';

/**
 * TopNav
 *
 * Horizontal top navigation bar.
 * Contains: breadcrumbs, global search, notification bell, user menu.
 *
 * TODO (axial-rebrand):
 *  - Wire breadcrumbs from React Router location
 *  - Connect notification bell to NotificationContext unread count
 *  - User dropdown: profile, settings, logout
 *
 * Props:
 *   onSidebarToggle — callback to toggle sidebar from mobile
 */
function TopNav({ onSidebarToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { segments: extraSegments } = useBreadcrumb();
  const { unseenCount } = useNotifications();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const breadcrumbSegments = React.useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const labels = {
      dashboard: 'Dashboard',
      projects: 'Projects',
      permits: 'Permits',
      tasks: 'Tasks',
      reviews: 'Reviews',
      drawings: 'Drawings',
        teams: 'Teams',
      qa: 'QA',
      notifications: 'Notifications',
      reports: 'Reports',
      settings: 'Settings',
      admin: 'Admin',
      transmittals: 'Transmittals'
    };

return parts
  .filter((part, index) => {
    const previousPart = parts[index - 1];

    // Hide UUID route params for known resource detail routes.
    if (
      (previousPart === 'projects' || previousPart === 'permits') &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(part)
    ) {
      return false;
    }

    return true;
  })
  .map((part, index, filteredParts) => ({
    label: labels[part] || part,
    href: `/${filteredParts.slice(0, index + 1).join('/')}`,
  }));
  }, [location.pathname]);

  const displayName =
    userProfile?.display_name ||
    [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') ||
    'User';
  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || 'U';

  function clearLocalAuthStorage() {
    try {
      const keysToRemove = [];
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (key && /^sb-.*-auth-token$/i.test(key)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach((key) => window.localStorage.removeItem(key));
      window.localStorage.removeItem('activeTenantId');
    } catch (error) {
      console.warn('Failed to clear local auth storage during logout:', error);
    }
  }

  const handleLogout = async () => {
    setMenuOpen(false);

    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      if (error) {
        console.warn('Supabase local sign-out returned an error:', error.message);
      }
    } catch (error) {
      console.warn('Supabase sign-out failed, continuing with local cleanup:', error);
    } finally {
      clearLocalAuthStorage();
      navigate('/auth', { replace: true });
    }
  };

  return (
    <header
      className="h-14 flex items-center gap-4 px-4 flex-shrink-0 z-50"
      style={{ background: 'var(--axial-bg)', borderBottom: '1px solid var(--axial-border)' }}
    >
      {/* Mobile sidebar toggle */}
      <button
        onClick={onSidebarToggle}
        className="md:hidden text-gray-400 hover:text-gray-100"
        aria-label="Open sidebar"
      >
        ☰
      </button>

      {/* Breadcrumb area */}
      <nav className="flex items-center gap-1 text-sm text-gray-400 min-w-0">
        <Link to="/dashboard" className="hover:text-gray-100 transition-colors flex-shrink-0">
          Home
        </Link>
        {breadcrumbSegments.map(segment => (
          <React.Fragment key={segment.href}>
            <span className="text-gray-600">/</span>
            <Link to={segment.href} className="hover:text-gray-100 transition-colors truncate">
              {segment.label}
            </Link>
          </React.Fragment>
        ))}
        {extraSegments.map((segment, i) => (
          <React.Fragment key={`extra-${i}`}>
            <span className="text-gray-600">/</span>
            {segment.onClick ? (
              <button
                type="button"
                onClick={segment.onClick}
                className="hover:text-gray-100 transition-colors truncate"
              >
                {segment.label}
              </button>
            ) : (
              <span className="text-gray-300 truncate">{segment.label}</span>
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* Search bar */}
      <div className="flex-1 flex justify-center px-4">
        <AppSearch
          value={search}
          onChange={setSearch}
          placeholder="Search projects, reviews, tasks..."
          shortcutHint="⌘ K"
          className="w-full max-w-xl"
        />
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Notification bell (SVG, blue badge) */}
        <Link
          to="/notifications"
          className="relative text-gray-400 hover:text-gray-100 transition-colors"
          aria-label="Notifications"
        >
          <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2Zm6-6V10a6 6 0 1 0-12 0v6l-2 2v1h16v-1l-2-2Z" stroke="#94a3b8" strokeWidth="1.5" fill="none"/>
          </svg>
          {unseenCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 shadow-sm">
              {unseenCount > 99 ? '99+' : unseenCount}
            </span>
          )}
        </Link>

        {/* User avatar placeholder */}
        <div className="relative">
          <button
            className="w-8 h-8 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center"
            aria-label="User menu"
            onClick={() => setMenuOpen(open => !open)}
          >
            {initials}
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-2 w-52 rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
              <div className="px-3 py-2 border-b border-gray-700">
                <p className="text-sm text-gray-100 truncate">{displayName}</p>
              </div>
              <div className="p-1">
                <Link to="/settings" className="block rounded px-3 py-2 text-sm text-gray-300 hover:bg-gray-800">
                  Settings
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="block w-full text-left rounded px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
                >
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default TopNav;
